import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import Marquee from "react-fast-marquee";
import {
  ArrowRight, Database, Activity, Network, Brain, ShieldCheck, Sparkles, Terminal, CircleDot,
  Globe2, FileCheck2, Video, Handshake, Mail, Palette, MessageCircle, LineChart, Quote,
  ShieldAlert, Radio, ChevronRight, Zap, Lock, Maximize2, X,
} from "lucide-react";
import BookCallDialog from "@/components/BookCallDialog";
import PublicScorecardPreview from "@/components/PublicScorecardPreview";
import CaseStudyRotator from "@/components/CaseStudyRotator";
import BookDemoButton from "@/components/BookDemoButton";
import VideoAd from "@/components/VideoAd";
import HomepageSignalStrip from "@/components/HomepageSignalStrip";
import usePageview from "@/lib/usePageview";

// Phase 25.13an — Video ad asset URLs (hosted on Emergent CDN).
// Two distinct ads for two audiences:
//   • EXECUTIVE: enterprise / C-suite pitch → played on /, /ventia
//   • SMB:        SMB / POS merchant pitch → played on /founders-guide,
//                 plus an SMB-paramount block on / (SOURCES section) and
//                 /pricing (above the tier cards).
const VIDEO_EXEC = "https://customer-assets.emergentagent.com/job_analytics-vault-5/artifacts/3fzn18bh_output.mp4";
const VIDEO_SMB = "https://customer-assets.emergentagent.com/job_analytics-vault-5/artifacts/1np457in_DARA%20%E2%80%94%20Enterprise%20AI%20Data%20Monetization%20%281%29.mp4";

const CLIENTS = [
  "GLOBAL RETAILERS", "MID-MARKET BANKS", "MINING MAJORS", "HOSPITALITY GROUPS",
  "COMMODITY TRADERS", "F&B CHAINS", "INDEPENDENT ADVISORS", "POS-DRIVEN SMBs",
  "CROSS-BORDER OPERATORS", "TREASURY DESKS", "ENERGY OPERATORS", "AGRI EXPORTERS",
  "FAMILY OFFICES", "MARKET INFRASTRUCTURE", "REGULATED FINTECHS",
];

// Ten modules — every feature shipped through Phase 22.4.
const FEATURES = [
  { icon: Database, title: "Unified Data Capture", desc: "Ingest CSV, POS, CRM, IoT, ERP and supply-chain streams. Auto-profiles every column, surfaces drift on re-upload." },
  { icon: Brain, title: "DARA Insight Engine", desc: "DARA Engine surfaces trends, anomalies and predictions — each quantified with a revenue-impact line." },
  { icon: Network, title: "Decision Mapping", desc: "Visual pathways: Data → DARA Synthesis → Decision Brief. Every opportunity ranked by ease, time-to-value and regulator-grade evidence shape." },
  { icon: FileCheck2, title: "Market Playbook Scorecards", desc: "Score your CSVs against RBI · NPCI · CBN · SARB · Udyam rules. Localised benchmarks, pass / warn / fail, regulator citations." },
  { icon: Globe2, title: "Localised Intelligence", desc: "Workspaces run in ₦ · ₹ · R · R$ · A$ · $ · € · £. Every insight, briefing and proposal quotes in your currency, scaled to your market." },
  { icon: Video, title: "Walkthrough Recorder", desc: "One-click screen + voice capture. DARA auto-transcribes and suggests highlights. Embed on ABM pages or deal rooms." },
  { icon: Handshake, title: "ABM Pages + Auto Deal Rooms", desc: "Account-specific landing pages with live ROI calculators. Cross the threshold → DARA auto-spawns a branded deal room with a DARA-drafted proposal." },
  { icon: Mail, title: "Branded Email + Ask DARA", desc: "Resend-powered outbound in your brand. Prospects reply → threads land in DARA. Inbox weaponised as a discovery loop." },
  { icon: Palette, title: "Brand Kit (White-label)", desc: "Inject your logo and accent into every public page, deal room and PDF. Distribute DARA under your own brand." },
  { icon: MessageCircle, title: "DARA Assistant", desc: "Converse with your data. Ask revenue questions, get strategic answers — every claim cited back to the source CSV." },
];

// Three currently-shipped market playbooks. More to come in 2026.
// Phase 25.13i — Interactive jurisdictional playbooks. Each entry has
// optional `image` + `extendedDesc` + `dataFlow` that surfaces in the modal
// triggered by hover-and-click on the public landing playbooks grid.

// Phase 25.16ax — Trust strip + verticals strip card components.
function TrustCard({ code, title, body }) {
  return (
    <div className="bg-black p-6 sm:p-8 flex flex-col" data-testid={`trust-card-${code}`}>
      <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">
        // {code}
      </div>
      <h3 className="font-heading font-bold text-lg tracking-tight mb-3">{title}</h3>
      <p className="text-sm text-zinc-300 leading-relaxed">{body}</p>
    </div>
  );
}

function VerticalCard({ tag, status, href, desc }) {
  const isPublic = status === "public";
  const isContact = status === "contact";
  const labelText = isPublic ? "VIEW PAGE →" : isContact ? "CONTACT US →" : "BY INTRODUCTION";
  const labelColor = isPublic || isContact ? "text-[#FFB000]" : "text-zinc-500";
  const content = (
    <div className={`bg-black p-6 flex flex-col h-full ${(isPublic || isContact) ? "hover:bg-zinc-950 cursor-pointer transition-colors" : ""}`}>
      <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-2">
        // {tag}
      </div>
      <p className="text-sm text-zinc-300 leading-relaxed mb-4 flex-1">{desc}</p>
      <div className={`font-mono text-[11px] uppercase tracking-widest mt-auto ${labelColor}`}>
        {labelText}
      </div>
    </div>
  );
  return (
    <div data-testid={`vertical-${tag.toLowerCase().replace(/\s+/g, "-")}`}>
      {(isPublic || isContact) && href ? (
        <Link to={href} className="block h-full">{content}</Link>
      ) : (
        content
      )}
    </div>
  );
}

const PLAYBOOKS = [
  {
    flag: "NG", market: "Nigeria Banking", currency: "NGN ₦",
    regulators: ["CBN", "NIBSS", "NDIC", "BVN"],
    desc: "8 benchmarks derived from CBN prudential limits, NIBSS transaction corridors and NDIC deposit bands. Lagos concentration, SME turnover, BVN completeness, phone-format validation.",
    rules: 8,
    lastRefreshed: "12 May 2026",
    status: "Live · Production",
    image: "https://customer-assets.emergentagent.com/job_analytics-vault-5/artifacts/v5ipa5zn_Lagos%20data.png",
    imageAlt: "DARA Africa — Lagos Port data flow with Central Bank of Nigeria regulatory stamp",
    imageCaption: "Lagos Port · CBN-aligned data flow",
    extendedDesc: "Built ground-up against CBN's 2024 KYC framework. Our Lagos Port data flow ingests live ingestion from shipping lines, terminal operators, NCS customs, and NDIC-regulated banks. The rulebook flags BVN completeness gaps, NIBSS settlement-corridor anomalies, and Lagos-concentration risk before they trigger CBN compliance escalations. Live for Nigerian banks, fintechs, and importer-exporters.",
    dataFlow: ["Shipping lines · Terminal operators", "Customs (NCS) · Port authorities", "Banks & Payments · Insurers", "→ Real-time validation + CBN-aligned rules"],
  },
  {
    flag: "ZA", market: "South Africa Banking", currency: "ZAR R",
    regulators: ["SARB", "SARS", "POPIA", "PASA"],
    desc: "Big-Four concentration, POPIA consent tracking, SARS tax-number validation, PASA acquirer rails, Rand earnings bands against SARB economic indicators.",
    rules: 8,
    lastRefreshed: "09 May 2026",
    status: "Live · Production",
    image: "https://customer-assets.emergentagent.com/job_analytics-vault-5/artifacts/srr8zbdy_ME.png",
    imageAlt: "DARA — Global Rule Book Sovereign Manual covering SARB and 4 other central banks",
    imageCaption: "Global Sovereign Manual · SARB chapter",
    extendedDesc: "South Africa's banking sector is one of the most concentrated in the world — the Big Four (Standard Bank, Absa, FirstRand, Nedbank) control roughly 80% of deposits. DARA's SA rulebook fingerprints Big-Four exposure, tracks POPIA-compliant consent capture, and validates SARS tax-number format (NN-NNNNNNNN-C-NN) on every customer-data ingestion. Rand-earnings bands cross-reference against SARB monthly indicators.",
    dataFlow: ["PASA acquirer rails (Visa/Mastercard/PayShap)", "SARB economic indicators feed", "POPIA consent register", "→ Sovereign-manual compliance scoring"],
  },
  {
    flag: "IN", market: "India Fintech & MSME", currency: "INR ₹",
    regulators: ["RBI", "NPCI", "MSMED", "UIDAI"],
    desc: "Tier-1 metro share, PSB vs private bank mix, UPI vs legacy rails, MSMED turnover bands, RBI KYC completeness, Income-Tax PAN format (AAAAA9999A).",
    rules: 8,
    lastRefreshed: "11 May 2026",
    status: "Live · Production",
    image: "https://customer-assets.emergentagent.com/job_analytics-vault-5/artifacts/7z2c4zi2_image.png",
    imageAlt: "DARA India — The Gulf Corridor · Bridging the $21B India-Gulf remittance gap · Aadhaar e-KYC + RBI Regulatory Framework + DARA Corridor Card",
    imageCaption: "India ↔ Gulf · $21B remittance corridor",
    extendedDesc: "India's payments rails moved 460 billion transactions through UPI in the past 12 months — and the India ↔ Gulf remittance corridor alone carries USD 21B/year (the single biggest inbound corridor into India). DARA reads UPI · IMPS · NEFT · RTGS · DIFC partner-bank rails simultaneously, scores Tier-1 metro vs Tier-2/3 expansion, breaks PSU bank vs private bank vs payments-bank mix, and fingerprints the Gulf corridor specifically (UAE · KSA · Qatar · Oman · Kuwait · Bahrain remitter share). MSMED Section-7 turnover-band classification ($75K / $10M / $50M / $250M) flows through to RBI-aligned reporting. UIDAI Aadhaar e-KYC is integrated via the masked-Aadhaar consent flow — every customer-data ingest stamps consent timestamp + IP for FIU audit. PAN format validated on every contact. Optional DARA Corridor Card surfaces the FX/settlement audit trail per remittance.",
    dataFlow: ["UPI · IMPS · NEFT · RTGS · DIFC rails", "Gulf corridor share · UAE · KSA · Qatar · Oman", "UIDAI Aadhaar e-KYC · masked-Aadhaar consent", "MSMED turnover bands · PAN validation · FIU audit", "→ RBI + DIFC corridor + Aadhaar-compliant"],
  },
  {
    flag: "BR", market: "Brazil Pix & MEI", currency: "BRL R$",
    regulators: ["BACEN", "Pix", "LGPD", "Receita"],
    desc: "SP-RJ corridor concentration, Big-5 bank share (Itaú/Bradesco/Santander/BB/Caixa), Pix vs TED/boleto mix, MEI/ME/EPP turnover bands, LGPD completeness, CNPJ format validation.",
    rules: 8,
    lastRefreshed: "07 May 2026",
    status: "Live · Production",
    image: "https://customer-assets.emergentagent.com/job_analytics-vault-5/artifacts/srr8zbdy_ME.png",
    imageAlt: "DARA — Global Rule Book Sovereign Manual covering Brazil and 4 other central banks",
    imageCaption: "Global Sovereign Manual · BACEN chapter",
    extendedDesc: "Brazil's Pix instant-payment rail processed BRL 27 trillion in 2024 — already 80% of all electronic payments. DARA's BACEN-aligned rulebook fingerprints Pix-versus-TED-versus-boleto rail mix, scores SP-RJ corridor concentration risk, and validates CNPJ on every transaction (NN.NNN.NNN/NNNN-NN). LGPD consent capture is logged with timestamp and source URL for every customer-data ingestion. MEI / ME / EPP turnover-band classification follows Receita Federal Annex VII.",
    dataFlow: ["Pix · TED · boleto rail-by-rail breakdown", "Big-5 bank concentration scoring", "LGPD consent audit · CNPJ validation", "→ BACEN + Receita-aligned compliance"],
  },
  {
    flag: "AU", market: "Australia Banking", currency: "AUD A$",
    regulators: ["APRA", "AUSTRAC", "ATO", "OAIC"],
    desc: "Carrying-statement parity across the Big-4 ADIs (CBA · Westpac · NAB · ANZ). APRA capital adequacy + concentration, NPP/PayID vs legacy BECS rails, ATO small-business bands, Privacy Act completeness, ABN validation.",
    rules: 8,
    lastRefreshed: "13 May 2026",
    status: "Live · Production",
    image: "https://customer-assets.emergentagent.com/job_analytics-vault-5/artifacts/rrkao3ew_image.png",
    imageAlt: "DARA OS — The Independent Verification Layer for Australian Banking (CBA · Westpac · NAB · ANZ · RBA-certified)",
    imageCaption: "Big-4 ADI carrying-statement coverage · RBA-aligned",
    extendedDesc: "Australia's deposit base is concentrated in the Big-4 ADIs — CBA · Westpac · NAB · ANZ — holding ~75% of total deposits and reporting against APRA's identical CPS 220 / CPS 230 carrying-statement framework. DARA's AU rulebook reads each bank's carrying-statement structure (capital adequacy, liquidity coverage ratio, concentration exposure, operational-risk capital) and benchmarks merchant exposure against APRA-published industry averages. NPP/PayID vs legacy BECS rail mix is fingerprinted on every transaction. AUSTRAC threshold-transaction reporting is automated. Privacy Act consent capture logs IP + timestamp. ATO small-business turnover bands ($75K / $10M / $50M / $250M) flow through to BAS-ready reporting. ABN is validated on every customer ingest.",
    dataFlow: ["Big-4 ADI carrying-statement parity · CPS 220 / CPS 230", "NPP · PayID · BECS rail-by-rail breakdown", "AUSTRAC threshold reporting · Privacy Act IP-stamped consent", "ATO turnover bands · BAS-ready ABN-validated", "→ APRA-aligned + BAS-ready compliance"],
  },
];

// Phase 25.16ax — Public price tags removed from landing 2026-06-02.
// Founder note: "the price tag for starters is scaring people away. So
// we should remove all the pricing." Replaced with a three-shape
// commercial strip (Paid POC · Annual · Strategic Partner) — no
// numbers, all CTAs route to /pricing (founder conversation) or
// /pos-live (the only visibly-priced surface, $50/mo SMB self-serve).
const COMMERCIAL_SHAPES = [
  {
    code: "01",
    name: "Paid Proof-of-Concept",
    cadence: "90 days · one pillar · one workspace",
    blurb: "Most operators start here. Small enough to clear procurement quickly, structured enough to prove the daily brief on a single regulatory or operating pillar. Fee credits 100% toward your annual contract if you continue.",
    feats: [
      "One workspace · one pillar · one jurisdiction",
      "Daily DARA Engine brief · live audit log",
      "Standard MNDA in place day one",
      "Fee credits forward on conversion",
    ],
    cta: "Scope a POC",
  },
  {
    code: "02",
    name: "Annual Operating Contract",
    cadence: "12 months · multi-pillar · multi-workspace",
    blurb: "The default shape once a POC converts. Scales with the regulatory surface you sit under (AML · RG · advertising · integrity for wagering; JORC · work-programme · native-title for exploration; the equivalent stack for whichever vertical you run).",
    feats: [
      "Multiple workspaces · unlimited integrations",
      "Expert Network jurisdictional rulebooks",
      "Operations Desk success engineer",
      "Quarterly deep-dive review sessions",
    ],
    cta: "Talk to DARA team",
    featured: true,
  },
  {
    code: "03",
    name: "Strategic Operating Partner",
    cadence: "Multi-year · sovereign · air-gapped option",
    blurb: "For regulated, sovereign, or category-defining operators. Air-gapped deployment available, custom rulebooks authored on request, white-label and reseller arrangements, direct line to the DARA team. Conversation-led commercials, papered properly.",
    feats: [
      "Air-gapped / sovereign deployment option",
      "Custom rulebooks authored on request",
      "SOC 2 path · custom DPA · white-label",
      "Direct line to DARA team",
    ],
    cta: "Talk to DARA team",
  },
];

export default function Landing() {
  const nav = useNavigate();
  usePageview("/");
  const [callDialog, setCallDialog] = useState({ open: false, interest: "enterprise" });
  const [playbookFocus, setPlaybookFocus] = useState(null); // hovered playbook (preview card)
  const [playbookOpen, setPlaybookOpen] = useState(null);   // clicked playbook (lightbox modal)

  // ESC closes the playbook lightbox
  useEffect(() => {
    if (!playbookOpen) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setPlaybookOpen(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [playbookOpen]);
  const goPricing = () => {
    // All commercial conversations now route through /pricing (no public price list).
    nav("/pricing");
  };
  return (
    <div className="min-h-screen bg-black text-white" data-testid="landing-page">
      {/* NAV */}
      <nav className="sticky top-0 z-50 border-b border-zinc-900 bg-black/90 backdrop-blur">
        <div className="max-w-[1400px] mx-auto flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-2" data-testid="brand">
            <Terminal className="w-5 h-5 text-[#FFB000]" />
            <span className="font-heading font-black text-xl tracking-tighter">DARA</span>
            <span className="font-mono text-[11px] sm:text-[10px] text-zinc-500 ml-2 hidden sm:inline">// DATA × DARA × DECISIONS</span>
          </div>
          <div className="hidden md:flex items-center gap-6 font-mono text-[11px] uppercase tracking-widest text-zinc-400">
            <a href="#founder" className="hover:text-white transition-colors">Founder</a>
            <a href="#modules" className="hover:text-white transition-colors">Platform</a>
            <a href="#sources" className="hover:text-white transition-colors">Sources</a>
            <a href="#playbooks" className="hover:text-white transition-colors">Playbooks</a>
            <a href="#analysts" className="hover:text-white transition-colors">Analysts</a>
            <a href="#operations" className="hover:text-white transition-colors">Operations</a>
            <a href="#customers" className="hover:text-white transition-colors">Customers</a>
            <a href="#try" className="hover:text-white transition-colors text-[#FFB000]">Try it</a>
            <Link to="/pricing" data-testid="nav-pricing" className="hover:text-white transition-colors">Talk to us</Link>
          </div>
          <div className="flex items-center gap-3">
            <Link to="/login" data-testid="nav-login" className="font-mono text-[11px] uppercase tracking-widest text-zinc-300 hover:text-white px-3 py-2">Login</Link>
            <Link
              to="/signup"
              data-testid="nav-signup"
              className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-wider px-4 py-2 hover:bg-[#E59E00] transition-colors"
            >
              Launch Terminal
            </Link>
          </div>
        </div>
      </nav>

      {/* HERO */}
      <header className="relative overflow-hidden border-b border-zinc-900">
        <div className="grid-noise absolute inset-0" />
        <div className="relative max-w-[1400px] mx-auto px-6 py-24 lg:py-32 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <div className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-widest text-[#FFB000] mb-6">
              <CircleDot className="w-3 h-3 pulse-dot" />
              <span>Live intelligence for global conglomerates · ₦ · ₹ · R · R$ · A$ · $</span>
            </div>
            <h1 className="font-heading font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.92] uppercase">
              The compliance + intelligence layer<br />
              <span className="text-[#FFB000]">above the tools you already use.</span>
            </h1>
            <p className="mt-8 max-w-xl text-zinc-400 text-base leading-relaxed">
              DARA sits above your POS, your ERP, your CRM, your IoT streams, your inspection platform — and produces the daily intelligence brief your team should have been getting all along. We don't move your data. We don't train on it. We don't see it without you logging the read. <span className="text-[#FFB000]">Your data stays in your tenant.</span>
            </p>
            <div className="mt-10 flex flex-wrap gap-4 items-center">
              <button
                onClick={() => nav("/signup")}
                data-testid="hero-start-btn"
                className="bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-wider px-6 py-3 hover:bg-[#E59E00] transition-colors flex items-center gap-2"
              >
                Start free trial <ArrowRight className="w-4 h-4" />
              </button>
              <a
                href="#try"
                data-testid="hero-try-btn"
                className="border border-[#FFB000]/40 text-[#FFB000] font-mono text-xs uppercase tracking-wider px-6 py-3 hover:bg-[#FFB000]/10 transition-colors flex items-center gap-2"
              >
                <Zap className="w-4 h-4" /> Try in 60 sec
              </a>
              <button
                onClick={() => setCallDialog({ open: true, interest: "enterprise" })}
                data-testid="hero-demo-btn"
                className="border border-zinc-700 text-white font-mono text-xs uppercase tracking-wider px-6 py-3 hover:bg-zinc-900 transition-colors"
              >
                Book an analyst
              </button>
              <span className="font-mono text-[11px] sm:text-[10px] text-zinc-600 ml-2">NO CARD · 14-DAY TRIAL · GDPR-COMPLIANT · TRUST CENTER LIVE</span>
            </div>

            {/* hero stat strip — DARA platform facts only, no customer-aggregate fictions */}
            <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900 max-w-2xl">
              {[
                ["MODULES SHIPPED", "9", "POS · books · treasury"],
                ["JURISDICTION PLAYBOOKS", "11", "AU · NZ · IN · NG · ZA · UK · …"],
                ["DARA ENGINE", "DARA Engine", "grounded on your data"],
              ].map(([l, v, s]) => (
                <div key={l} className="bg-black p-4">
                  <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500">{l}</div>
                  <div className="font-mono text-2xl font-medium mt-2 text-white">{v}</div>
                  <div className="font-mono text-[11px] sm:text-[10px] text-[#00E676] mt-1">{s}</div>
                </div>
              ))}
            </div>
          </div>

          {/* terminal preview — clearly labelled as illustrative sample */}
          <div className="lg:col-span-5 panel relative overflow-hidden">
            <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-900 bg-[#080808]">
              <div className="w-2 h-2 bg-[#FF3B30]" />
              <div className="w-2 h-2 bg-[#FFB000]" />
              <div className="w-2 h-2 bg-[#00E676]" />
              <span className="font-mono text-[11px] sm:text-[10px] text-zinc-500 ml-3">DARA://terminal · <span className="text-[#FFB000]">// SAMPLE OUTPUT — illustrative, not customer data</span></span>
            </div>
            <div className="p-4 font-mono text-[11px] leading-6 text-zinc-300 min-h-[340px]">
              <div className="text-[#FFB000]">$ dara connect --csv=sample_bank_crm.csv</div>
              <div className="text-zinc-500">{'>'} 10,480 rows · auto-detected Nigeria market · currency ₦</div>
              <div className="text-[#FFB000] mt-3">$ dara scorecard --playbook=nigeria_banking</div>
              <div className="text-zinc-400">▣ PASS    <span className="text-[#00E676]">BVN format 99.3% · CBN compliant</span></div>
              <div className="text-zinc-400">▣ WARN    <span className="text-[#FFB000]">Lagos concentration 81% — diversify to Kano / PH</span></div>
              <div className="text-zinc-400">▣ PASS    <span className="text-[#00E676]">Monthly × 12 ≈ Annual · NDIC ready</span></div>
              <div className="text-[#FFB000] mt-3">$ dara synthesize --brief</div>
              <div className="text-zinc-400">{'['}CRM{']'} ──► {'['}Tier-1 Concentration Risk{']'} ──► <span className="text-[#FFB000]">WARN · CBN-cited</span></div>
              <div className="text-zinc-400">{'['}POS{']'} ──► {'['}SME Churn Probability{']'} ──► <span className="text-[#FFB000]">14 accounts flagged</span></div>
              <div className="text-zinc-400">{'['}IOT{']'} ──► {'['}ATM Uptime Forecast{']'} ──► <span className="text-[#FFB000]">3-site degradation</span></div>
              <div className="text-[#FFB000] mt-3">$ <span className="cursor-blink">▋</span></div>
              <div className="mt-6 pt-3 border-t border-zinc-900 text-zinc-600 text-[9px] uppercase tracking-widest">
                Sample data shown for demonstration · real workspace outputs vary by your sources
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Phase 25.16ax — TRUST STRIP · confident data-handling answer
          to the "are you a data mining company" question founder kept
          fielding. Confident architectural framing, not defensive
          "your data is safe" framing. */}
      <section className="border-b border-zinc-900 bg-[#0a0a0a]" data-testid="trust-strip-section">
        <div className="max-w-[1400px] mx-auto px-6 py-12 sm:py-16">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-3">
            // HOW DARA HANDLES YOUR DATA · ARCHITECTED, NOT PROMISED
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl leading-tight mb-8">
            Your data stays in your tenant. <span className="text-[#FFB000]">Always.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900" data-testid="trust-strip-grid">
            <TrustCard
              code="01"
              title="Your tenant. Your keys."
              body="Every workspace is provisioned with its own AES-256 encryption key. We don't have access to your data unless you log the read. Architecturally enforced at the database layer — not by policy, not by promise."
            />
            <TrustCard
              code="02"
              title="We don't train on it."
              body="Your operational data never feeds a machine-learning model — ours or anyone else's. The DARA Engine reads your data live, produces the brief, and forgets. The training signal that improves DARA comes from anonymised feedback loops, never your raw data."
            />
            <TrustCard
              code="03"
              title="Audit every read."
              body="Every interaction with your data writes to your audit log inside your workspace. You can see what we saw, when, and why. The audit trail is regulator-grade — built to hold up under AUSTRAC, UKGC, BSR, APRA, FCA, FinCEN."
            />
          </div>
          <p className="mt-6 text-xs sm:text-[11px] text-zinc-500 leading-relaxed italic max-w-3xl">
            We sit above the tools you already use — your POS, ERP, inspection platform, IoT stream — and produce the daily intelligence layer they should have been generating. We're not a data mining company. We're the synthesis layer above your existing data, owned by you, gated by you, auditable by you.
          </p>
        </div>
      </section>

      {/* Phase 25.16ax — VERTICALS STRIP · show breadth without exposing
          bespoke pages. Locked entries with "by introduction only" so
          founder's warm channels stay private but visitors see the
          category coverage. */}
      <section className="border-b border-zinc-900" data-testid="verticals-strip-section">
        <div className="max-w-[1400px] mx-auto px-6 py-14 sm:py-20">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-3">
            // WHO DARA IS BUILT FOR · SEVEN VERTICALS · ONE TERMINAL
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight max-w-3xl leading-tight">
            One synthesis layer. <span className="text-[#FFB000]">Seven operational realities.</span>
          </h2>
          <p className="mt-4 text-sm sm:text-base text-zinc-300 max-w-3xl leading-relaxed">
            The DARA Engine is the same product across every industry — what changes is the rulebook coverage, the data ingestion shape, and the regulator surface. Below is the current vertical coverage. Some surfaces are public. Some are introduction-only because we're already in conversations with the operator and their data isn't ours to share publicly.
          </p>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900" data-testid="verticals-grid">
            <VerticalCard tag="WAGERING" status="public" href="/wagering"
              desc="AML/CTF · Responsible Gambling · Integrity · Advertising. Sports books and casinos across AU / UK / US / EU. Live category page." />
            <VerticalCard tag="POS LIVE" status="public" href="/pos-live"
              desc="Hospitality + retail SMB tier. Live POS feed → daily intelligence brief in plain English. Visible monthly pricing. Self-serve trial." />
            <VerticalCard tag="INDUSTRIAL" status="public" href="/industrial-demo"
              desc="Mining / water / heavy industry operational synthesis. SCADA · OPC UA · IoT ingestion layer." />
            <VerticalCard tag="EXPLORATION" status="public" href="/exploration-trial"
              desc="Mineral exploration synthesis: drill assays · geochem · geophysics · JORC continuous evidence · competitor watch. Self-serve live trial — plug your overnight assays in, get the brief in 60 seconds." />
            <VerticalCard tag="ENERGY ARBITRAGE" status="introduction"
              desc="Industrial energy consumers: RERT · FCAS · WDRM · LGC · BTM Generation arbitrage stack synthesis. By introduction only." />
            <VerticalCard tag="UK FIRE SAFETY" status="introduction"
              desc="Building Safety Act 2022 · BS 8214 · BS 9999 continuous compliance evidence for fire-safety operators. By introduction only." />
            <VerticalCard tag="AU INSURANCE CHANNEL" status="introduction"
              desc="Insurer-distributed compliance evidence layer for contractor-liability portfolios. By introduction only." />
            <VerticalCard tag="ANALYST MARKETPLACE" status="public" href="/experts"
              desc="Senior data analysts referring clients into DARA earn lifetime-recurring commissions per closed referral. Live programme." />
            <VerticalCard tag="MORE VERTICALS" status="contact" href="/pricing"
              desc="Healthcare, education, defence, agriculture, financial services. The same synthesis layer applies. Talk to us about your vertical." />
          </div>
        </div>
      </section>

      {/* Phase 25.13an — EXECUTIVE VIDEO AD (~30s, no card required pitch).
          Sits immediately after the hero terminal so high-intent enterprise
          visitors hit the founder pitch within 6s of landing. Muted by
          default; user-controlled play. Phase 25.13an-p2 — widened to
          match hero content width per user feedback. */}
      <section className="border-b border-zinc-900 py-12 sm:py-16 bg-zinc-950/40" data-testid="exec-video-section">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-3 text-center">
            // 30 SECONDS · FROM THE FOUNDER
          </div>
          <h2 className="font-heading font-black text-2xl sm:text-3xl tracking-tight text-center max-w-2xl mx-auto">
            Why your data is worth <span className="text-[#FFB000]">billions</span> — and what it costs you to find out.
          </h2>
          <div className="mt-6">
            <VideoAd
              src={VIDEO_EXEC}
              label="// FOUNDER · 30s · PRESS PLAY"
              caption="// SOUND OFF BY DEFAULT · CLICK SPEAKER ICON FOR AUDIO · 14-DAY FREE TRIAL · NO CARD REQUIRED"
              testId="hero-video-exec"
            />
          </div>
        </div>
      </section>

      {/* Phase 25.16r — Live signal strip · founder-editable from
          /app/admin/system. Signals "this thing is moving" to any
          visitor without revealing operator names or playbook. */}
      <HomepageSignalStrip />

      {/* OPERATOR CATEGORIES — descriptive ICP, not customer logos */}
      <section className="border-b border-zinc-900 py-8">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-600 text-center mb-6">
            Built for the data-intensive enterprise
          </div>
          <Marquee speed={30} gradient={false}>
            {CLIENTS.concat(CLIENTS).map((c, i) => (
              <span key={i} className="font-heading font-black text-2xl tracking-tight text-zinc-600 px-10 hover:text-zinc-300 transition-colors">
                {c}
              </span>
            ))}
          </Marquee>
        </div>
      </section>

      {/* Phase 25.16q — TWO VERTICALS surface.
          Surfaces DARA Industrial and DARA Wagering as equally-weighted
          entry points without revealing the playbook. NO operator names,
          NO specific architecture, NO pricing, NO playbook. High-level
          framing only — designed to convert inbound, not educate
          competitors. */}
      <section className="border-b border-zinc-900 py-20 sm:py-24 bg-[#0a0a0a]" data-testid="verticals-section">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-3 flex items-center gap-2">
            <Terminal className="w-3.5 h-3.5" /> // TWO PRODUCT LINES · ONE INTELLIGENCE LAYER
          </div>
          <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05] max-w-4xl">
            Operators who run the world's data estates trust DARA in <span className="text-[#FFB000]">two places</span>.
          </h2>
          <p className="mt-4 text-sm sm:text-base text-zinc-400 max-w-3xl leading-relaxed">
            Same intelligence engine. Different language per audience. Different regulators per jurisdiction. One daily brief per operator.
          </p>

          <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900">
            {/* TILE 1 — INDUSTRIAL */}
            <div className="bg-black p-8 sm:p-10 flex flex-col group hover:bg-[#0a0a0a] transition-colors" data-testid="vertical-industrial">
              <div className="flex items-center gap-3 mb-4">
                <Activity className="w-6 h-6 text-[#FFB000]" />
                <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-[0.25em] text-zinc-500">// PRODUCT LINE 01</span>
              </div>
              <h3 className="font-heading font-black text-2xl sm:text-3xl tracking-tight mb-2">DARA Industrial</h3>
              <p className="text-sm font-mono uppercase tracking-widest text-[#FFB000] mb-4">
                For services · utilities · resources · infrastructure
              </p>
              <p className="text-sm sm:text-base text-zinc-300 leading-relaxed mb-6">
                The read-only intelligence layer for organisations that run hundreds of sites and dozens of telemetry dialects. We don't replace your historian or SCADA — we sit above them.
              </p>
              <p className="text-xs text-zinc-500 leading-relaxed mb-8 italic">
                Active conversations across AU/NZ Tier-1 services and utility operators.
              </p>
              <div className="mt-auto flex items-center justify-between">
                <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-600">Public landing</span>
                <Link
                  to="/industries/mining"
                  data-testid="vertical-industrial-cta"
                  className="font-mono text-xs uppercase tracking-widest text-[#FFB000] group-hover:text-white flex items-center gap-1.5"
                >
                  Explore <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>
            </div>

            {/* TILE 2 — WAGERING */}
            <div className="bg-black p-8 sm:p-10 flex flex-col group hover:bg-[#0a0a0a] transition-colors" data-testid="vertical-wagering">
              <div className="flex items-center gap-3 mb-4">
                <ShieldAlert className="w-6 h-6 text-[#FFB000]" />
                <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-[0.25em] text-zinc-500">// PRODUCT LINE 02</span>
              </div>
              <h3 className="font-heading font-black text-2xl sm:text-3xl tracking-tight mb-2">DARA Wagering</h3>
              <p className="text-sm font-mono uppercase tracking-widest text-[#FFB000] mb-4">
                For sports betting operators · AU · UK · EU · US
              </p>
              <p className="text-sm sm:text-base text-zinc-300 leading-relaxed mb-6">
                The regulator-grade intelligence layer for operators carrying a wagering licence. One morning brief, one audit trail — every regulator surface, every jurisdiction.
              </p>
              <p className="text-xs text-zinc-500 leading-relaxed mb-8 italic">
                Active conversations with AU social bookmaker design partner. Multi-jurisdictional architecture.
              </p>
              <div className="mt-auto flex items-center justify-between">
                <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-600">Public landing</span>
                <Link
                  to="/wagering"
                  data-testid="vertical-wagering-cta"
                  className="font-mono text-xs uppercase tracking-widest text-[#FFB000] group-hover:text-white flex items-center gap-1.5"
                >
                  Explore <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-0.5 transition-transform" />
                </Link>
              </div>
            </div>
          </div>

          <p className="mt-8 text-xs text-zinc-600 max-w-3xl font-mono leading-relaxed">
            // BOTH PRODUCT LINES SHARE: same engine · same operating posture · sovereign deployment · read-only ingest · regulator-grade audit trail · daily brief delivery model.
          </p>
        </div>
      </section>

      {/* Phase 25.13s — DATA SOURCES (POS-agnostic framing).
          Reframes the platform from "Square plug-in" to "any-POS intelligence
          layer". Visually weighs Xero / MYOB / CSV equal to Square. Reassures
          non-Square merchants that they're not excluded. */}
      <section id="sources" className="border-b border-zinc-900 py-20" data-testid="data-sources-section">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="flex items-center gap-2 font-mono text-[11px] sm:text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-3">
            <Database className="w-3.5 h-3.5" /> // DATA SOURCES · POS-AGNOSTIC
          </div>
          <div className="grid lg:grid-cols-12 gap-10 items-end">
            <div className="lg:col-span-7">
              <h2 className="font-heading font-black text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-[1.05]">
                Whatever POS you run.<br/>
                <span className="text-[#FFB000]">DARA reads it.</span>
              </h2>
              <p className="text-base text-zinc-400 mt-5 max-w-2xl leading-relaxed">
                You don't need to be on Square to use DARA. Three native OAuth integrations
                cover ~80% of the AU/NZ SMB POS landscape via the accounting layer alone
                (Xero + MYOB), and the universal CSV path lets any other system — Stripe
                Terminal, Lightspeed, Tyro, Toast, Clover, your bank-issued EFTPOS terminal —
                feed DARA in under 60 seconds.
              </p>
            </div>
            <div className="lg:col-span-5 panel p-4 font-mono text-[11px] leading-relaxed">
              <div className="text-[#FFB000] mb-2">// COVERAGE MATH</div>
              <div className="space-y-1 text-zinc-400">
                <div>· Xero LIVE  → ~62% of AU SMBs reconcile here</div>
                <div>· MYOB LIVE  → ~24% of AU SMBs (Australia-native)</div>
                <div>· Square LIVE → +6% direct, real-time</div>
                <div>· CSV upload  → <span className="text-[#00E676]">every other POS, instantly</span></div>
                <div className="pt-2 mt-2 border-t border-zinc-900 text-[#FFB000]">≈ 95% of AU SMB merchant data is reachable today.</div>
              </div>
            </div>
          </div>

          {/* Source grid — equal visual weight for the four ingestion paths */}
          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900">
            {[
              {
                name: "XERO",
                status: "LIVE",
                accent: "#00E676",
                blurb: "OAuth · invoices · banking · accounts receivable",
                tag: "Universal accounting layer",
              },
              {
                name: "MYOB",
                status: "LIVE",
                accent: "#00E676",
                blurb: "OAuth · daily sales · cash position",
                tag: "AU-native accounting",
              },
              {
                name: "SQUARE",
                status: "LIVE",
                accent: "#00E676",
                blurb: "OAuth · POS Live · real-time transactions",
                tag: "Direct POS feed",
              },
              {
                name: "CSV · ANY POS",
                status: "INSTANT",
                accent: "#FFB000",
                blurb: "Stripe · Lightspeed · Tyro · Toast · Clover · bank EFTPOS",
                tag: "60-sec drag-and-drop",
              },
            ].map((s) => (
              <div key={s.name} className="bg-black p-6" data-testid={`source-card-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                <div className="flex items-center justify-between mb-3">
                  <div className="font-heading font-black text-lg tracking-tight">{s.name}</div>
                  <span className="font-mono text-[11px] sm:text-[10px] sm:text-[9px] uppercase tracking-widest px-1.5 py-0.5 border" style={{ borderColor: s.accent + "55", color: s.accent, backgroundColor: s.accent + "11" }}>
                    {s.status}
                  </span>
                </div>
                <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-2">{s.tag}</div>
                <p className="font-mono text-[11px] sm:text-[10px] text-zinc-500 leading-relaxed">{s.blurb}</p>
              </div>
            ))}
          </div>

          {/* Reassurance row — already on X? line + roadmap */}
          <div className="mt-8 grid md:grid-cols-2 gap-6">
            <div className="border border-zinc-900 bg-zinc-950/30 p-5">
              <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-2">// ALREADY ON ANOTHER POS?</div>
              <p className="font-mono text-xs sm:text-[11px] text-zinc-300 leading-relaxed">
                Drag your daily sales export into DARA today — the platform auto-detects
                your schema (transactions / customers / inventory), flags compliance gaps
                against your jurisdiction's rulebook, and ships your first DARA brief within
                the same session. No data engineer required.
              </p>
            </div>
            <div className="border border-zinc-900 bg-zinc-950/30 p-5">
              <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-2">// NATIVE INTEGRATION ROADMAP · 2026</div>
              <ul className="font-mono text-xs sm:text-[11px] text-zinc-300 leading-relaxed space-y-1">
                <li className="flex items-center gap-2"><span className="text-[#FFB000]">▸</span> <strong className="text-white">Stripe Terminal</strong> · 3-week build · Q2 2026</li>
                <li className="flex items-center gap-2"><span className="text-[#FFB000]">▸</span> <strong className="text-white">Tyro</strong> (NAB-owned EFTPOS) · pilot conversations open · Q3 2026</li>
                <li className="flex items-center gap-2"><span className="text-[#FFB000]">▸</span> <strong className="text-white">Lightspeed Retail</strong> · post-Stripe sequencing · Q3 2026</li>
              </ul>
            </div>
          </div>

          {/* Phase 25.13an-p4 — SMB VIDEO. Lives at the bottom of the
              SOURCES / POS-agnostic section so SMB-shaped visitors who
              self-identified through this content immediately get the
              23s pitch tailored to them. */}
          <div className="mt-12" data-testid="landing-smb-video-section">
            <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-[0.3em] text-[#FFB000] mb-3 text-center">
              // 23 SECONDS · FOR POS-DRIVEN OPERATORS
            </div>
            <h3 className="font-heading font-black text-xl sm:text-2xl tracking-tight text-center max-w-2xl mx-auto mb-6">
              You're capturing data. <span className="text-[#FFB000]">Are you making money from it?</span>
            </h3>
            <VideoAd
              src={VIDEO_SMB}
              label="// SMB · 23s · WATCH"
              caption="// 11 JURISDICTION PLAYBOOKS · AUTO-DETECTS YOUR MARKET · BOOST REVENUE WITH DARA"
              testId="landing-smb-video"
            />
          </div>
        </div>
      </section>

      {/* FROM THE FOUNDER — vision note from the founder, balanced with company depth */}
      <section id="founder" className="border-b border-zinc-900 py-24 bg-[#040404]" data-testid="founder-section">
        <div className="max-w-[1400px] mx-auto px-6 grid lg:grid-cols-12 gap-10 items-center">
          {/* Photo — left */}
          <div className="lg:col-span-5 relative">
            <div className="relative aspect-square overflow-hidden border border-zinc-900">
              <img
                src="https://customer-assets.emergentagent.com/job_analytics-vault-5/artifacts/6e2zkdej_maji.png"
                alt="Emmanuel Maji, founder of DARA OS"
                loading="lazy"
                className="w-full h-full object-cover grayscale-[15%] hover:grayscale-0 transition-all duration-700"
                data-testid="founder-photo"
              />
              <div className="absolute inset-0 bg-gradient-to-tr from-black/40 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-black via-black/70 to-transparent">
                <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-1">SYDNEY · AUSTRALIA</div>
                <div className="font-heading font-black text-xl tracking-tight">Emmanuel Maji</div>
                <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-400 mt-0.5">Founder & CEO, DARA OS</div>
              </div>
            </div>
          </div>

          {/* Note — right */}
          <div className="lg:col-span-7">
            <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-4">// 01 · FROM THE FOUNDER</div>
            <h2 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tighter leading-[0.95] mb-8 max-w-2xl">
              We built DARA OS because<br />
              <span className="text-[#FFB000]">the gap was personal.</span>
            </h2>
            <div className="space-y-5 text-zinc-300 text-base leading-relaxed max-w-2xl">
              <p>
                For years we watched mid-market operators in retail, hospitality, and treasury pay
                either <span className="text-white font-semibold">$24,000 a year for a Bloomberg terminal they barely used</span> — or
                stitch together five spreadsheets every Monday because the only real synthesis
                alternative was a <span className="text-white font-semibold">$500K+ Palantir engagement</span> that would never come.
              </p>
              <p>
                Both options were broken. DARA OS exists to close that gap — engineered as a
                <span className="text-white font-semibold"> three-tier intelligence architecture</span>: a DARA Engine
                synthesis layer for speed and breadth, an internal Operations Desk headquartered in
                Sydney for daily oversight and customer success, and an Expert Network of
                senior data analysts in formal partnership with DARA who maintain jurisdictional
                rulebooks and handle deep human analysis where judgment matters.
              </p>
              <p>
                Every line of code, every DARA prompt, every rulebook clause, and every analyst
                contract was shipped against a real operating need — not a roadmap slide. Founding
                customers get <span className="text-[#FFB000] font-semibold">direct access to me</span>, the analyst lead assigned to their
                workspace, and the rulebook authors covering their jurisdiction.
              </p>
            </div>

            <div className="mt-10 flex flex-wrap gap-4 items-center">
              <button
                onClick={() => setCallDialog({ open: true, interest: "founder" })}
                data-testid="founder-book-call-btn"
                className="bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-wider px-6 py-3 hover:bg-[#E59E00] transition-colors flex items-center gap-2"
              >
                Book a 25-min call <ArrowRight className="w-4 h-4" />
              </button>
              <Link
                to="/founding"
                data-testid="founder-founding-link"
                className="border border-[#FFB000]/40 text-[#FFB000] font-mono text-xs uppercase tracking-wider px-6 py-3 hover:bg-[#FFB000]/10 transition-colors flex items-center gap-2"
              >
                See the founding offer <ChevronRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="mt-10 pt-6 border-t border-zinc-900 grid grid-cols-1 sm:grid-cols-3 gap-px bg-zinc-900 max-w-2xl">
              {[
                ["HEADQUARTERS", "Sydney, AU"],
                ["MODULES IN PRODUCTION", "9"],
                ["JURISDICTION RULEBOOKS", "11"],
              ].map(([l, v]) => (
                <div key={l} className="bg-[#040404] p-3">
                  <div className="font-mono text-[11px] sm:text-[10px] sm:text-[9px] uppercase tracking-widest text-zinc-500">{l}</div>
                  <div className="font-mono text-sm font-medium mt-1 text-white">{v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CASE STUDIES — auto-rotating, hides when no published studies (Phase 25.2d) */}
      <CaseStudyRotator />

      {/* FEATURES — 10 modules */}
      <section id="modules" className="border-b border-zinc-900 py-24">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="flex items-end justify-between gap-4 mb-12 flex-wrap">
            <div>
              <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">02 · THE PLATFORM</div>
              <h2 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tighter max-w-3xl">
                Ten modules.<br />One operational nerve center.
              </h2>
            </div>
            <p className="text-zinc-500 text-sm max-w-md">
              Every module installs independently. Composable. API-first. Built for enterprises
              measured in continents, not cities.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-px bg-zinc-900 border border-zinc-900">
            {FEATURES.map((f, i) => (
              <div key={i} className="bg-black p-6 hover:bg-[#080808] transition-colors group" data-testid={`feature-${i}`}>
                <f.icon className="w-5 h-5 text-[#FFB000] mb-4 stroke-[1.5]" />
                <div className="font-mono text-[11px] sm:text-[10px] sm:text-[9px] text-zinc-600 mb-2">MODULE_{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-heading font-bold text-base mb-2">{f.title}</h3>
                <p className="text-xs text-zinc-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PLAYBOOKS — market coverage with cross-border rulebooks visual */}
      <section id="playbooks" className="border-b border-zinc-900 py-24 relative">
        <div className="grid-noise absolute inset-0 opacity-30" />
        <div className="relative max-w-[1400px] mx-auto px-6">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">03 · REGULATOR-GRADE COVERAGE</div>
          <h2 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tighter max-w-3xl mb-4">
            Your data, scored against the rules that matter.
          </h2>
          <p className="text-zinc-400 max-w-2xl mb-12 text-sm leading-relaxed">
            Generic AI tools don&apos;t know which regulator runs your market, which metros skew your
            concentration risk, or which number format your tax filing expects. DARA does. Eight
            benchmarks per market, cited to the exact regulator circular — for every jurisdiction
            you operate in.
          </p>

          {/* Cross-border compliance hero — rulebooks visual */}
          <div className="relative border border-zinc-900 mb-12 overflow-hidden bg-[#020817]" data-testid="playbooks-rulebooks-hero">
            <img
              src="https://customer-assets.emergentagent.com/job_analytics-vault-5/artifacts/hamqfjtt_image.png"
              alt="DARA cross-border rulebooks — RBI, CBN, SARB and global coverage"
              loading="lazy"
              className="w-full h-auto object-cover"
            />
            <div className="absolute top-0 left-0 right-0 px-5 py-3 bg-gradient-to-b from-black/80 to-transparent">
              <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000]">// CROSS-BORDER COMPLIANCE · LOCALISED INTELLIGENCE</div>
            </div>
            <div className="px-6 py-5 border-t border-zinc-900 bg-black grid sm:grid-cols-4 gap-px">
              {[
                ["REGULATORY INTELLIGENCE", "Real-time updates & risk alerts"],
                ["LOCAL EXPERTISE", "On-the-ground knowledge across key jurisdictions"],
                ["CROSS-BORDER ALIGNMENT", "Harmonising compliance across markets"],
                ["TRUSTED PARTNERSHIPS", "Collaborative · transparent · accountable"],
              ].map(([l, s]) => (
                <div key={l} className="bg-black p-1 pr-3">
                  <div className="font-mono text-[11px] sm:text-[10px] sm:text-[9px] uppercase tracking-widest text-[#FFB000]">{l}</div>
                  <div className="font-mono text-[11px] sm:text-[10px] text-zinc-400 mt-1 leading-snug">{s}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="flex items-center gap-3 mb-3 font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500" data-testid="playbooks-interaction-hint">
            <CircleDot className="w-3 h-3 text-[#FFB000] pulse-dot" />
            <span>Hover any rulebook to preview the live data map · <span className="text-[#FFB000]">click to expand</span></span>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-5 gap-px bg-zinc-900 border border-zinc-900" data-testid="playbooks-grid">
            {PLAYBOOKS.map((pb) => {
              const isFocused = playbookFocus === pb.flag;
              return (
                <button
                  type="button"
                  key={pb.flag}
                  onMouseEnter={() => setPlaybookFocus(pb.flag)}
                  onMouseLeave={() => setPlaybookFocus((cur) => (cur === pb.flag ? null : cur))}
                  onFocus={() => setPlaybookFocus(pb.flag)}
                  onBlur={() => setPlaybookFocus((cur) => (cur === pb.flag ? null : cur))}
                  onClick={() => setPlaybookOpen(pb)}
                  data-testid={`playbook-${pb.flag.toLowerCase()}`}
                  aria-label={`Open ${pb.market} jurisdictional snapshot`}
                  className={`group relative text-left bg-black p-8 transition-all duration-300 outline-none focus-visible:ring-2 focus-visible:ring-[#FFB000] ${
                    isFocused
                      ? "bg-[#0a0700] shadow-[inset_0_0_0_1px_rgba(255,176,0,0.6),0_0_42px_-6px_rgba(255,176,0,0.45)] -translate-y-[2px] z-20"
                      : "hover:bg-[#070500]"
                  }`}
                >
                  {/* corner ticker */}
                  <div className={`absolute top-2 right-2 font-mono text-[8px] uppercase tracking-widest transition-colors ${isFocused ? "text-[#FFB000]" : "text-zinc-700 group-hover:text-zinc-500"}`}>
                    {isFocused ? "▣ LIVE PREVIEW" : "▢ HOVER · CLICK"}
                  </div>

                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 border flex items-center justify-center font-heading font-black text-lg transition-colors ${isFocused ? "border-[#FFB000] text-[#FFB000] bg-[#1a1300]" : "border-zinc-800 text-[#FFB000] bg-[#0f0b00]"}`}>
                      {pb.flag}
                    </div>
                    <div>
                      <div className="font-heading font-black text-xl tracking-tight">{pb.market}</div>
                      <div className="font-mono text-[11px] sm:text-[10px] text-zinc-500 uppercase tracking-widest">{pb.currency} · {pb.rules} rules</div>
                    </div>
                  </div>

                  {/* description swaps to inline thumbnail on hover */}
                  <div className="relative min-h-[120px]">
                    <p className={`text-sm text-zinc-400 leading-relaxed transition-opacity duration-200 ${isFocused ? "opacity-0" : "opacity-100"}`}>
                      {pb.desc}
                    </p>
                    {/* thumbnail preview reveal */}
                    <div className={`absolute inset-0 flex flex-col gap-2 transition-all duration-300 ${isFocused ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2 pointer-events-none"}`}>
                      <div className="relative border border-[#FFB000]/40 bg-[#020817] overflow-hidden" style={{ aspectRatio: "16 / 10" }}>
                        <img
                          src={pb.image}
                          alt={pb.imageAlt}
                          loading="lazy"
                          className="absolute inset-0 w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent" />
                        <div className="absolute bottom-1.5 left-2 right-2 flex items-center justify-between">
                          <span className="font-mono text-[11px] sm:text-[10px] sm:text-[9px] uppercase tracking-widest text-[#FFB000]">{pb.imageCaption || pb.market}</span>
                          <span className="font-mono text-[8px] uppercase tracking-widest text-zinc-300 flex items-center gap-1">
                            <Maximize2 className="w-3 h-3" /> EXPAND
                          </span>
                        </div>
                      </div>
                      <p className="font-mono text-[11px] sm:text-[10px] leading-snug text-zinc-400 line-clamp-3">
                        {pb.dataFlow && pb.dataFlow[0]}
                      </p>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2 mt-5">
                    {pb.regulators.map((r) => (
                      <span key={r} className={`font-mono text-[11px] sm:text-[10px] sm:text-[9px] uppercase tracking-widest px-2 py-1 border transition-colors ${isFocused ? "border-[#FFB000] text-black bg-[#FFB000]" : "border-[#FFB000]/30 text-[#FFB000] bg-[#FFB000]/5"}`}>
                        {r}
                      </span>
                    ))}
                  </div>
                </button>
              );
            })}
          </div>
          <div className="mt-6 text-center">
            <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-600">
              Each rulebook authored & maintained by senior analysts in the Expert Network · Kenya (CBK) · UAE (DIFC) · EU (GDPR) playbooks in Q3 2026
            </span>
          </div>
        </div>
      </section>

      {/* DECISION FLOW */}
      <section id="decisions" className="border-b border-zinc-900 py-24 relative">
        <div className="relative max-w-[1400px] mx-auto px-6">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">04 · DATA → DECISIONS</div>
          <h2 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tighter max-w-3xl mb-16">
            We show you exactly where the next decision lives.
          </h2>
          <div className="grid lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
            {[
              { step: "01", label: "CAPTURE", heading: "Every signal, every system", body: "CSV, POS, CRM, IoT fleets, ERP ledgers, supply-chain graphs. Stream or batch. Tenant-isolated. Drift-aware on every re-upload." },
              { step: "02", label: "SYNTHESIZE", heading: "DARA + human review", body: "Trends. Anomalies. Predictions. Every model quantifies confidence and operational impact in your context. A named analyst QAs the output before it lands in your inbox." },
              { step: "03", label: "DECIDE", heading: "Intelligence as an output", body: "Visual flow maps: Data In → DARA Synthesis → Decision Brief. Every opportunity ranked by ease, time-to-value, and regulator-grade evidence shape. The brief lands in your inbox — never in a third-party marketplace." },
            ].map((s) => (
              <div key={s.step} className="bg-black p-10">
                <div className="flex items-center gap-3 mb-6">
                  <span className="font-mono text-5xl font-black text-[#FFB000]">{s.step}</span>
                  <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500">{s.label}</span>
                </div>
                <h3 className="font-heading font-bold text-2xl mb-4">{s.heading}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ANALYSTS — Expert Network depth (senior analysts in formal partnership) */}
      <section id="analysts" className="border-b border-zinc-900 py-24 bg-[#040404]">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="flex items-end justify-between gap-4 mb-12 flex-wrap">
            <div>
              <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">05 · THE EXPERT NETWORK</div>
              <h2 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tighter max-w-3xl">
                DARA runs the engine.<br />Senior analysts run the judgment.
              </h2>
            </div>
            <p className="text-zinc-500 text-sm max-w-md">
              Layer 03 of the DARA architecture is the Expert Network — senior data analysts in
              formal partnership with DARA under our Expert Programme. They author jurisdictional
              rulebooks, run deep-dive sessions, and grow with the platform.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
            {/* Card 1 — How it works */}
            <div className="bg-black p-8" data-testid="experts-how-card">
              <FileCheck2 className="w-6 h-6 text-[#FFB000] mb-5" />
              <h3 className="font-heading font-black text-xl mb-3">Formal partnership structure.</h3>
              <p className="text-sm text-zinc-400 leading-relaxed mb-5">
                Every analyst in the network signs a formal partnership agreement —
                NSW-governed, 16 clauses, executed through DARA&apos;s in-house e-signature flow.
                Confidentiality, data-handling protocols, IP assignment, and conduct standards
                are codified before any customer data is reviewed.
              </p>
              <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#00E676] border-t border-zinc-900 pt-3">
                ▸ Master partnership agreement · NSW · 16 clauses<br />▸ Data-handling + confidentiality codified
              </div>
            </div>

            {/* Card 2 — What they do */}
            <div className="bg-black p-8" data-testid="experts-role-card">
              <Brain className="w-6 h-6 text-[#FFB000] mb-5" />
              <h3 className="font-heading font-black text-xl mb-3">Where humans matter most.</h3>
              <p className="text-sm text-zinc-400 leading-relaxed mb-5">
                AI handles the daily synthesis. The Expert Network handles the judgment work —
                authoring jurisdictional rulebooks (RBI, CBN, SARB, APRA), running deep-dive
                sessions for treasury and risk decisions, and reviewing edge cases the AI escalates.
              </p>
              <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#00E676] border-t border-zinc-900 pt-3">
                ▸ Rulebook authorship<br />▸ Live expert sessions<br />▸ AI-escalation review
              </div>
            </div>

            {/* Card 3 — How they earn */}
            <div className="bg-black p-8 flex flex-col" data-testid="experts-earn-card">
              <Handshake className="w-6 h-6 text-[#FFB000] mb-5" />
              <h3 className="font-heading font-black text-xl mb-3">Aligned incentives.</h3>
              <p className="text-sm text-zinc-400 leading-relaxed mb-5">
                <span className="text-white font-semibold">20% MRR commission</span> on every paying customer the analyst refers, paid for as long
                as the customer stays subscribed. <span className="text-white font-semibold">60% revenue share</span> on live expert sessions
                booked through the marketplace. Senior tier unlocks at 5 referrals + 4.5 NPS.
              </p>
              <Link
                to="/experts"
                data-testid="analyst-bench-cta"
                className="font-mono text-[11px] uppercase tracking-widest px-5 py-3 border border-[#FFB000]/40 text-[#FFB000] hover:bg-[#FFB000]/10 flex items-center justify-center gap-2 mt-auto"
              >
                Apply to the network <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
          </div>

          {/* Strip below — public leaderboard reference */}
          <div className="mt-6 grid sm:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
            {[
              ["TIERS", "Standard · Senior", "Senior unlocks at 5 referrals + 4.5 NPS"],
              ["LEADERBOARD", "Public Top-10", "Privacy opt-out available · earners only"],
              ["GOVERNANCE", "NSW law", "16-clause master agreement · 3 schedules"],
            ].map(([l, v, s]) => (
              <div key={l} className="bg-black p-5">
                <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{l}</div>
                <div className="font-heading font-black text-base text-white">{v}</div>
                <div className="font-mono text-[11px] sm:text-[10px] text-[#00E676] mt-1">{s}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OPERATIONS — three-layer intelligence architecture (depth narrative) */}
      <section id="operations" className="border-b border-zinc-900 relative overflow-hidden">
        <div className="relative">
          <div className="relative max-w-[1400px] mx-auto px-6 py-24 w-full">
            <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">06 · THE DARA OPERATING ARCHITECTURE</div>
            <h2 className="font-heading font-black text-4xl sm:text-6xl uppercase tracking-tighter max-w-4xl mb-6">
              Three layers of intelligence.<br /><span className="text-[#FFB000]">One operating system.</span>
            </h2>
            <p className="text-zinc-400 max-w-3xl text-base leading-relaxed mb-14">
              DARA is not a chatbot bolted to a dashboard. The platform is engineered as a layered
              intelligence stack — frontier AI handles speed and breadth, an internal Operations
              Desk handles continuity and customer success, and an Expert Network of senior data
              analysts in formal partnership with DARA handles the judgment calls that machines
              should not make alone. Each layer carries an explicit, documented role.
            </p>

            <div className="grid lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900" data-testid="ops-architecture">
              {/* LAYER 1 — AI Synthesis */}
              <div className="bg-black p-8 relative" data-testid="ops-layer-ai">
                <div className="flex items-center gap-3 mb-5">
                  <div className="font-mono text-[11px] sm:text-[10px] sm:text-[9px] uppercase tracking-widest text-[#FFB000] px-2 py-1 border border-[#FFB000]/40 bg-[#FFB000]/5">LAYER 01</div>
                  <Brain className="w-5 h-5 text-[#FFB000]" />
                </div>
                <h3 className="font-heading font-black text-2xl mb-3">AI Synthesis Layer</h3>
                <p className="text-sm text-zinc-400 leading-relaxed mb-5">
                  DARA Engine runs grounded on your real workspace data — POS feeds,
                  accounting books, treasury positions, commodity benchmarks. Daily briefings,
                  anomaly detection, and natural-language workspace queries all flow through this
                  layer. No training on customer data.
                </p>
                <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500 border-t border-zinc-900 pt-3">
                  <div className="text-[#00E676]">▸ DARA Engine</div>
                  <div className="text-[#00E676]">▸ Grounded on real workspace data</div>
                  <div className="text-[#00E676]">▸ Daily briefing · query interface</div>
                </div>
              </div>

              {/* LAYER 2 — Operations Desk */}
              <div className="bg-black p-8 relative" data-testid="ops-layer-desk">
                <div className="flex items-center gap-3 mb-5">
                  <div className="font-mono text-[11px] sm:text-[10px] sm:text-[9px] uppercase tracking-widest text-[#FFB000] px-2 py-1 border border-[#FFB000]/40 bg-[#FFB000]/5">LAYER 02</div>
                  <Radio className="w-5 h-5 text-[#FFB000]" />
                </div>
                <h3 className="font-heading font-black text-2xl mb-3">Operations Desk</h3>
                <p className="text-sm text-zinc-400 leading-relaxed mb-5">
                  Sydney-headquartered internal team responsible for customer onboarding, day-to-day
                  workspace oversight, integration health-checks across Square / Xero / MYOB / Stripe,
                  and incident response. Founder-led, with a defined hiring plan funded by the seed
                  round.
                </p>
                <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500 border-t border-zinc-900 pt-3">
                  <div className="text-[#00E676]">▸ Sydney HQ · AEDT business hours</div>
                  <div className="text-[#00E676]">▸ Customer success · integration health</div>
                  <div className="text-[#00E676]">▸ Direct founder access for founding cohort</div>
                </div>
              </div>

              {/* LAYER 3 — Expert Network */}
              <div className="bg-black p-8 relative" data-testid="ops-layer-network">
                <div className="flex items-center gap-3 mb-5">
                  <div className="font-mono text-[11px] sm:text-[10px] sm:text-[9px] uppercase tracking-widest text-[#FFB000] px-2 py-1 border border-[#FFB000]/40 bg-[#FFB000]/5">LAYER 03</div>
                  <Handshake className="w-5 h-5 text-[#FFB000]" />
                </div>
                <h3 className="font-heading font-black text-2xl mb-3">Expert Network</h3>
                <p className="text-sm text-zinc-400 leading-relaxed mb-5">
                  Senior data analysts in formal partnership with DARA under our Expert
                  Programme — each governed by a master partnership agreement (NSW law, 16
                  clauses, in-house e-signature flow) covering confidentiality, data handling, and
                  conduct standards. They author jurisdictional rulebooks, run deep-dive sessions,
                  and grow with the platform.
                </p>
                <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500 border-t border-zinc-900 pt-3">
                  <div className="text-[#00E676]">▸ Partnership agreements · NSW law</div>
                  <div className="text-[#00E676]">▸ 11 jurisdictional rulebooks maintained</div>
                  <div className="text-[#00E676]">▸ Confidentiality + data-handling clauses</div>
                </div>
              </div>
            </div>

            {/* Architecture stats strip */}
            <div className="mt-10 grid sm:grid-cols-4 gap-px bg-zinc-900 border border-zinc-900">
              {[
                { icon: Brain, l: "DARA ENGINE", v: "DARA Engine", s: "Grounded · no training on your data" },
                { icon: Radio, l: "OPS DESK", v: "Sydney HQ", s: "AU/NZ time zones covered" },
                { icon: Handshake, l: "EXPERT NETWORK", v: "In partnership", s: "11 jurisdictions · data-handling clauses" },
                { icon: ShieldCheck, l: "GOVERNANCE", v: "Trust Center", s: "GDPR · TOTP 2FA · audit trails" },
              ].map(({ icon: Icon, l, v, s }) => (
                <div key={l} className="bg-black p-5">
                  <Icon className="w-4 h-4 text-[#FFB000] mb-3" />
                  <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500 mb-1">{l}</div>
                  <div className="font-heading font-black text-xl text-white">{v}</div>
                  <div className="font-mono text-[11px] sm:text-[10px] text-[#00E676] mt-1">{s}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* DIFFERENTIATION — Phase 25.13as · honest comparison strip.
          Defensive positioning: when prospects think "I'll just use ChatGPT"
          or "we already have Power BI", show them line-by-line what's actually
          different. No vendor logos (legal-safe). Bloomberg-style table. */}
      <section id="difference" className="border-b border-zinc-900 py-24 bg-black" data-testid="difference-section">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">// 07 · WHAT MAKES DARA DIFFERENT</div>
          <h2 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tighter max-w-4xl mb-6">
            Not a chatbot. Not a dashboard. <span className="text-[#FFB000]">An operator terminal.</span>
          </h2>
          <p className="text-zinc-400 max-w-3xl text-base leading-relaxed mb-12">
            Most teams considering DARA ask the same fair question: <span className="text-zinc-200">why not just use ChatGPT, or extend our existing BI tool?</span> Honest answer in one table. No screenshots of competitors, no vendor-bashing — just the operational shape of each option, written down so you can decide.
          </p>

          {/* Comparison grid */}
          <div className="border border-zinc-900 overflow-x-auto" data-testid="difference-table">
            <table className="w-full text-xs min-w-[900px]">
              <thead>
                <tr className="bg-zinc-950 font-mono text-[11px] sm:text-[10px] sm:text-[9px] uppercase tracking-widest text-zinc-500">
                  <th className="text-left p-4 border-b border-zinc-900 w-[18%]">Dimension</th>
                  <th className="text-left p-4 border-b border-zinc-900 w-[20%]">Generic AI chatbots</th>
                  <th className="text-left p-4 border-b border-zinc-900 w-[20%]">BI / dashboards</th>
                  <th className="text-left p-4 border-b border-zinc-900 w-[20%]">Single-tool AI copilots</th>
                  <th className="text-left p-4 border-b border-zinc-900 w-[22%] bg-[#FFB000]/10 text-[#FFB000]">DARA OS</th>
                </tr>
              </thead>
              <tbody>
                {[
                  {
                    dim: "Where your data lives",
                    generic: "You paste it in. Stays in the chatbot's training pipeline unless you pay for an enterprise tier.",
                    bi: "Sits in your warehouse. You build pipes from POS, banking, CRM to it. Maintenance is on you.",
                    copilot: "Locked inside one app (CRM, finance suite, design tool). Cross-tool questions are out of scope.",
                    dara: "Read-only connections to your POS, banking, accounting, CRM. DARA never copies — it queries in place. Multi-region sovereign (AU · UK · US).",
                  },
                  {
                    dim: "Who QAs the output",
                    generic: "Nobody. You verify every answer manually. Hallucinations stay your problem.",
                    bi: "You build the math. You audit the math. You sign off on the math. Tool gives you a canvas, not judgement.",
                    copilot: "Vendor's model behind a black box. No human reviews your specific output.",
                    dara: "Named human analyst on every quarterly engagement. QA'd before any insight lands in your inbox. Mistakes are owned by us, not you.",
                  },
                  {
                    dim: "Currency · jurisdiction",
                    generic: "USD English defaults. \"Regulatory awareness\" is generic and date-stamped to its training cutoff.",
                    bi: "Whatever you build. Localising tax rules, currency, regulator templates is your project.",
                    copilot: "Vendor's home locale. Localisation is a feature request you wait for.",
                    dara: "11 jurisdiction playbooks live (AU · NZ · IN · NG · ZA · UK · US · MY · ID · TH · VN). Right currency, right regulator, right rulebook clauses cited in every brief.",
                  },
                  {
                    dim: "Revenue impact per insight",
                    generic: "Vibes. \"This trend looks important.\" You translate to dollars.",
                    bi: "You build the dollar column. You decide the impact band. You explain it to the board.",
                    copilot: "Tool-level KPIs (tickets closed, leads scored). Not group-level P&L.",
                    dara: "Every insight quantified in your currency, with a revenue-impact band and a confidence score. Brief is written for the board, not the analyst.",
                  },
                  {
                    dim: "How it stays live",
                    generic: "You re-prompt. You compare answers. You forget last week's question.",
                    bi: "You build dashboards. You configure alerts. You maintain the threshold logic.",
                    copilot: "Inside one product's update cycle. Cross-tool monitoring needs a separate stack.",
                    dara: "Always-on. Weekly briefs land in your inbox. Anomaly alerts in real time. You don't ask DARA — DARA tells you.",
                  },
                  {
                    dim: "Your data, their training",
                    generic: "Most consumer tiers train on your inputs. Enterprise tiers cost separately and require legal review.",
                    bi: "Stays in your warehouse. No AI inference unless you bolt one on.",
                    copilot: "Vendor terms vary. Often opt-out is buried.",
                    dara: "Zero training on your data. 30-day prompt retention. Full data lineage on every output. Written into the terms, not the FAQ.",
                  },
                ].map((row, idx) => (
                  <tr key={row.dim} className={`border-b border-zinc-900 ${idx % 2 === 0 ? "bg-black" : "bg-zinc-950/30"}`} data-testid={`difference-row-${idx}`}>
                    <td className="p-4 font-mono text-[11px] sm:text-[10px] uppercase tracking-wider text-zinc-300 align-top">{row.dim}</td>
                    <td className="p-4 text-zinc-400 leading-relaxed align-top">{row.generic}</td>
                    <td className="p-4 text-zinc-400 leading-relaxed align-top">{row.bi}</td>
                    <td className="p-4 text-zinc-400 leading-relaxed align-top">{row.copilot}</td>
                    <td className="p-4 text-zinc-100 leading-relaxed align-top bg-[#FFB000]/[0.04] border-l border-[#FFB000]/30">{row.dara}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Honest footnote — what DARA is NOT */}
          <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900">
            <div className="bg-black p-6">
              <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500 mb-2">// DARA IS NOT</div>
              <ul className="space-y-2 text-sm text-zinc-300 leading-relaxed">
                <li>— a replacement for your accountant, lawyer, or board</li>
                <li>— a generic chatbot you can ask anything</li>
                <li>— a no-code BI tool to build pixel-perfect dashboards</li>
                <li>— a forecasting oracle (every output ships with a confidence range)</li>
              </ul>
            </div>
            <div className="bg-black p-6">
              <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-2">// DARA IS</div>
              <ul className="space-y-2 text-sm text-zinc-100 leading-relaxed">
                <li>— the operator terminal that turns scattered data into weekly operating decisions</li>
                <li>— a jurisdiction-aware analytical layer with a named human QA'ing every brief</li>
                <li>— always-on monitoring across every cost line, every cost centre, every entity</li>
                <li>— commercial-grade infrastructure (read-only · zero training · multi-region sovereign)</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CUSTOMERS — placeholder until real customer stories are captured */}
      <section id="customers" className="border-b border-zinc-900 py-24 bg-[#040404]">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">07 · CUSTOMER STORIES</div>
          <h2 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tighter max-w-3xl mb-6">
            Real testimonials, <span className="text-[#FFB000]">coming as we ramp.</span>
          </h2>
          <p className="text-zinc-400 max-w-2xl text-base leading-relaxed mb-12">
            DARA is in the founding-customer phase. We won&apos;t fabricate quotes or borrow logos —
            customer stories will appear here as paying customers publish their results, with their
            permission and in their words. Want to be one of the first?
          </p>

          <div className="grid lg:grid-cols-2 gap-px bg-zinc-900 border border-zinc-900">
            <div className="bg-black p-10">
              <Quote className="w-7 h-7 text-[#FFB000] mb-6" />
              <h3 className="font-heading font-black text-2xl mb-4">Founding-customer slots open.</h3>
              <p className="text-sm text-zinc-400 leading-relaxed mb-8">
                The first 25 paying customers get hands-on onboarding from our Operations Desk,
                an indefinite 30% discount on the Scale tier, direct input on the product roadmap,
                and the option to feature their story here as a published case study.
              </p>
              <Link
                to="/founding"
                data-testid="customers-founding-cta"
                className="font-mono text-[11px] uppercase tracking-widest px-5 py-3 border border-[#FFB000]/40 text-[#FFB000] hover:bg-[#FFB000]/10 inline-flex items-center gap-2"
              >
                See the founding offer <ChevronRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="bg-black p-10">
              <Handshake className="w-7 h-7 text-[#FFB000] mb-6" />
              <h3 className="font-heading font-black text-2xl mb-4">Already paying? Tell your story.</h3>
              <p className="text-sm text-zinc-400 leading-relaxed mb-8">
                If DARA is already running in your workspace and you&apos;d like to publish a short case
                study (real numbers, real outcomes, your wording — we never put words in your mouth),
                we&apos;d love to feature you here.
              </p>
              <button
                onClick={() => setCallDialog({ open: true, interest: "case-study" })}
                data-testid="customers-casestudy-cta"
                className="font-mono text-[11px] uppercase tracking-widest px-5 py-3 border border-zinc-700 text-white hover:bg-zinc-900 inline-flex items-center gap-2"
              >
                Talk to our team <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* TRY IT — public scorecard preview */}
      <PublicScorecardPreview />

      {/* COMMERCIAL SHAPES — three shapes, no public price tags.
          POS Live ($50/mo) keeps a visible price on its own page. */}
      <section id="pricing" className="border-b border-zinc-900 py-24" data-testid="landing-commercials-section">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">10 · COMMERCIALS</div>
          <h2 className="font-heading font-black text-4xl sm:text-5xl uppercase tracking-tighter mb-4 max-w-4xl">Three shapes. <span className="text-[#FFB000]">No public price list.</span></h2>
          <p className="text-zinc-400 text-sm sm:text-base max-w-3xl mb-12 leading-relaxed">
            Pricing depends on your scale, your regulator surface, and which pillars you need covered — so we don't anchor on one number. What we will tell you up front: most operators start with a Paid POC on a single pillar (90 days · fee credits forward) and convert into an annual contract once it lands. Conversation-led commercials, papered properly.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-zinc-900 border border-zinc-900">
            {COMMERCIAL_SHAPES.map((p) => (
              <div
                key={p.code}
                className={`p-8 flex flex-col ${p.featured ? "bg-[#0f0b00] border-l border-r border-[#FFB000]/30" : "bg-black"}`}
                data-testid={`commercial-shape-${p.code}`}
              >
                {p.featured && <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-3">◆ MOST OPERATORS LAND HERE</div>}
                <div className="font-mono text-[11px] uppercase tracking-widest text-zinc-500 mb-2">// SHAPE {p.code}</div>
                <h3 className="font-heading font-black text-2xl tracking-tight mb-2">{p.name}</h3>
                <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-4">{p.cadence}</div>
                <p className="text-sm text-zinc-400 mb-6 leading-relaxed">{p.blurb}</p>
                <ul className="space-y-2 mb-8 flex-1">
                  {p.feats.map((f) => (
                    <li key={f} className="font-mono text-xs sm:text-[11px] text-zinc-300 flex gap-2">
                      <span className="text-[#FFB000]">▸</span>{f}
                    </li>
                  ))}
                </ul>
                <button
                  onClick={goPricing}
                  data-testid={`commercial-shape-${p.code}-cta`}
                  className={`w-full py-3 font-mono text-[11px] uppercase tracking-wider transition-colors ${
                    p.featured
                      ? "bg-[#FFB000] text-black hover:bg-[#E59E00]"
                      : "border border-zinc-700 hover:bg-zinc-900"
                  }`}
                >
                  {p.cta} <ArrowRight className="w-3 h-3 inline ml-1" />
                </button>
              </div>
            ))}
          </div>

          {/* SMB carve-out — only visibly-priced surface in DARA */}
          <div className="mt-8 border border-[#FFB000]/30 bg-[#0f0b00] p-6 sm:p-8 flex flex-col sm:flex-row gap-6 items-start sm:items-center justify-between" data-testid="landing-poslive-callout">
            <div className="flex-1">
              <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-2">// SMB CARVE-OUT · POS LIVE</div>
              <h3 className="font-heading font-black text-xl sm:text-2xl tracking-tight mb-2">
                Running a café, bar, or small chain? <span className="text-[#FFB000]">There's a self-serve lane.</span>
              </h3>
              <p className="text-sm text-zinc-400 leading-relaxed max-w-2xl">
                POS Live is the one surface where DARA carries a visible monthly price — built for SMB hospitality and retail operators who want the daily intelligence brief without a procurement loop. Connect your POS, get tomorrow morning's brief.
              </p>
            </div>
            <Link
              to="/pos-live"
              data-testid="landing-poslive-cta"
              className="bg-[#FFB000] text-black font-bold font-mono text-xs uppercase tracking-wider px-6 py-3 hover:bg-[#E59E00] transition-colors flex items-center gap-2 whitespace-nowrap"
            >
              See POS Live <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 border-b border-zinc-900 relative overflow-hidden">
        <div className="grid-noise absolute inset-0 opacity-40" />
        <div className="relative max-w-[1400px] mx-auto px-6 flex flex-col items-start gap-6">
          <h2 className="font-heading font-black text-4xl sm:text-6xl uppercase tracking-tighter max-w-4xl">
            The intelligence is already in your data.<br />
            <span className="text-[#FFB000]">Let&apos;s surface it — in your currency.</span>
          </h2>
          <div className="flex flex-wrap gap-4">
            <button
              onClick={() => nav("/signup")}
              data-testid="cta-bottom-btn"
              className="bg-[#FFB000] text-black font-bold font-mono text-sm uppercase tracking-wider px-8 py-4 hover:bg-[#E59E00] transition-colors flex items-center gap-2"
            >
              Deploy the terminal <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={() => setCallDialog({ open: true, interest: "enterprise" })}
              className="border border-zinc-700 text-white font-mono text-sm uppercase tracking-wider px-8 py-4 hover:bg-zinc-900 transition-colors"
              data-testid="cta-book-btn"
            >
              Or book an analyst first
            </button>
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-16 border-t border-zinc-900 bg-black">
        <div className="max-w-[1400px] mx-auto px-6 grid md:grid-cols-4 gap-8">
          <div className="md:col-span-1">
            <div className="flex items-center gap-2 mb-4">
              <Terminal className="w-5 h-5 text-[#FFB000]" />
              <span className="font-heading font-black text-xl tracking-tighter">DARA</span>
            </div>
            <div className="font-mono text-[11px] sm:text-[10px] text-zinc-600 leading-relaxed">
              THE INTELLIGENCE LAYER<br />
              Built for data-intensive operators<br />
              ₦ · ₹ · R · R$ · A$ · $ · € · £
            </div>
          </div>
          <div>
            <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-4">Platform</div>
            <ul className="space-y-2 font-mono text-xs sm:text-[11px] text-zinc-400">
              <li><a href="#modules" className="hover:text-white">Modules</a></li>
              <li><a href="#playbooks" className="hover:text-white">Playbooks</a></li>
              <li><a href="#decisions" className="hover:text-white">Decisions</a></li>
              <li>
                <Link to="/industrial" className="hover:text-white inline-flex items-center gap-1.5" data-testid="footer-industrial-link">
                  Industrial Adapter
                  <span className="text-[8px] tracking-widest text-[#00E676] border border-[#00E676]/40 bg-[#00E676]/5 px-1 py-px">SOON</span>
                </Link>
              </li>
              <li>
                <Link to="/founders-guide" className="hover:text-white" data-testid="footer-founders-guide-link">
                  Founder's Guide
                </Link>
              </li>
              <li>
                <Link to="/why-dara" className="hover:text-white" data-testid="footer-why-dara-link">
                  Why DARA <span className="text-zinc-600">— not a Claude wrapper</span>
                </Link>
              </li>
              <li><Link to="/login" className="hover:text-white">Terminal login</Link></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-4">Industries</div>
            <ul className="space-y-2 font-mono text-xs sm:text-[11px] text-zinc-400">
              <li>
                <Link to="/industries/mining" className="hover:text-white" data-testid="footer-industry-mining">
                  Mining & Resources
                </Link>
              </li>
              <li>
                <Link to="/industries/shipping" className="hover:text-white" data-testid="footer-industry-shipping">
                  Shipping & Maritime
                </Link>
              </li>
              <li>
                <Link to="/industries/heavy-machinery" className="hover:text-white" data-testid="footer-industry-heavy-machinery">
                  Heavy Machinery
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-4">Company</div>
            <ul className="space-y-2 font-mono text-xs sm:text-[11px] text-zinc-400">
              <li><a href="#analysts" className="hover:text-white">Analysts</a></li>
              <li><a href="#operations" className="hover:text-white">Operations</a></li>
              <li><a href="#customers" className="hover:text-white">Customers</a></li>
              <li><Link to="/security" className="hover:text-white inline-flex items-center gap-1.5" data-testid="footer-security-link"><Lock className="w-3 h-3" /> Trust & Security</Link></li>
              <li><Link to="/pricing" className="hover:text-white" data-testid="footer-pricing-link">Pricing</Link></li>
              <li><Link to="/experts" className="hover:text-white" data-testid="footer-experts-link">Become a DARA Expert</Link></li>
              <li><Link to="/consent" className="hover:text-white" data-testid="footer-consent-link">Email Policy</Link></li>
              <li><Link to="/case-studies" className="hover:text-white" data-testid="footer-case-studies-link">Case Studies</Link></li>
              <li><Link to="/wall" className="hover:text-white" data-testid="footer-pulse-wall-link">Pulse Wall</Link></li>
              <li><Link to="/privacy" className="hover:text-white" data-testid="footer-privacy-link">Privacy Policy</Link></li>
              <li><Link to="/terms" className="hover:text-white" data-testid="footer-terms-link">Terms of Service</Link></li>
              <li><a href="mailto:hello@dara.ai" className="hover:text-white">hello@dara.ai</a></li>
            </ul>
          </div>
          <div>
            <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-4">Trust & Compliance</div>
            <ul className="space-y-2 font-mono text-xs sm:text-[11px] text-zinc-400">
              <li className="flex items-center gap-2"><ShieldCheck className="w-3 h-3 text-[#00E676]" /> GDPR-compliant data handling</li>
              <li className="flex items-center gap-2"><ShieldCheck className="w-3 h-3 text-[#00E676]" /> Email-consent audit trail</li>
              <li className="flex items-center gap-2"><ShieldCheck className="w-3 h-3 text-[#00E676]" /> TOTP 2FA · Trust Center live</li>
              <li className="flex items-center gap-2"><ShieldCheck className="w-3 h-3 text-[#FFB000]" /> SOC 2 Type I (in progress, 2026)</li>
              <li className="flex items-center gap-2"><Sparkles className="w-3 h-3 text-[#FFB000]" /> DARA Engine · no training on your data</li>
            </ul>
          </div>
        </div>
        <div className="max-w-[1400px] mx-auto px-6 mt-12 pt-6 border-t border-zinc-900 flex flex-wrap items-center justify-between gap-4 font-mono text-[11px] sm:text-[10px] text-zinc-700 uppercase tracking-widest">
          <div>© 2026 DARA SYSTEMS · THE INTELLIGENCE LAYER</div>
          <div>dara.ai</div>
        </div>
      </footer>

      <BookCallDialog
        open={callDialog.open}
        defaultInterest={callDialog.interest}
        onClose={() => setCallDialog({ open: false, interest: "enterprise" })}
      />

      {/* PLAYBOOK LIGHTBOX — full-screen jurisdictional snapshot */}
      {playbookOpen && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 sm:p-8 animate-in fade-in duration-150"
          onClick={() => setPlaybookOpen(null)}
          role="dialog"
          aria-modal="true"
          aria-label={`${playbookOpen.market} jurisdictional snapshot`}
          data-testid="playbook-lightbox"
        >
          <div
            className="relative bg-[#040404] border border-[#FFB000]/40 max-w-6xl w-full max-h-[92vh] overflow-y-auto shadow-[0_0_60px_-10px_rgba(255,176,0,0.4)]"
            onClick={(e) => e.stopPropagation()}
          >
            {/* terminal-style header */}
            <div className="sticky top-0 z-10 flex items-center justify-between gap-3 px-5 py-3 border-b border-zinc-900 bg-[#050505]">
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-[#FF3B30]" />
                <div className="w-2 h-2 bg-[#FFB000]" />
                <div className="w-2 h-2 bg-[#00E676]" />
                <span className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-500 ml-2 hidden sm:inline">
                  DARA://playbook · {playbookOpen.flag} · <span className="text-[#FFB000]">{playbookOpen.market}</span>
                </span>
              </div>
              <button
                type="button"
                onClick={() => setPlaybookOpen(null)}
                data-testid="playbook-lightbox-close"
                className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-zinc-400 hover:text-[#FFB000] flex items-center gap-1 transition-colors"
                aria-label="Close playbook snapshot"
              >
                CLOSE <X className="w-4 h-4" />
              </button>
            </div>

            <div className="grid lg:grid-cols-5 gap-0">
              {/* SNAPSHOT IMAGE */}
              <div className="lg:col-span-3 bg-[#020817] relative border-b lg:border-b-0 lg:border-r border-zinc-900">
                <img
                  src={playbookOpen.image}
                  alt={playbookOpen.imageAlt}
                  className="w-full h-auto object-contain max-h-[80vh]"
                />
                <div className="absolute top-2 left-2 font-mono text-[11px] sm:text-[10px] sm:text-[9px] uppercase tracking-widest text-[#FFB000] bg-black/70 px-2 py-1">
                  {playbookOpen.imageCaption || playbookOpen.market}
                </div>
              </div>

              {/* CONTEXT PANEL */}
              <div className="lg:col-span-2 p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-5">
                  <div className="w-14 h-14 border border-[#FFB000] flex items-center justify-center font-heading font-black text-2xl text-[#FFB000] bg-[#1a1300]">
                    {playbookOpen.flag}
                  </div>
                  <div>
                    <div className="font-heading font-black text-2xl tracking-tight">{playbookOpen.market}</div>
                    <div className="font-mono text-[11px] sm:text-[10px] text-zinc-500 uppercase tracking-widest">{playbookOpen.currency} · {playbookOpen.rules} benchmarks</div>
                  </div>
                </div>

                <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-2">// Regulators in scope</div>
                <div className="flex flex-wrap gap-2 mb-6">
                  {playbookOpen.regulators.map((r) => (
                    <span key={r} className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest px-2 py-1 border border-[#FFB000]/40 text-[#FFB000] bg-[#FFB000]/5">
                      {r}
                    </span>
                  ))}
                </div>

                {/* Compliance health score widget — proof-of-work indicator */}
                <div className="mb-6 border border-zinc-900 bg-[#020817]" data-testid="playbook-health">
                  <div className="flex items-center justify-between px-3 py-2 border-b border-zinc-900 bg-[#050505]">
                    <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000]">// Compliance health</div>
                    <div className="flex items-center gap-1.5 font-mono text-[11px] sm:text-[10px] sm:text-[9px] uppercase tracking-widest text-[#00E676]">
                      <CircleDot className="w-2.5 h-2.5 pulse-dot" /> {playbookOpen.status || "LIVE"}
                    </div>
                  </div>
                  <div className="grid grid-cols-3 gap-px bg-zinc-900">
                    <div className="bg-black p-3">
                      <div className="font-mono text-[11px] sm:text-[10px] sm:text-[9px] text-zinc-500 uppercase tracking-widest">Benchmarks</div>
                      <div className="font-mono text-xl text-white mt-1">{playbookOpen.rules}<span className="text-zinc-600">/{playbookOpen.rules}</span></div>
                      <div className="font-mono text-[11px] sm:text-[10px] sm:text-[9px] text-[#00E676] mt-0.5">Active</div>
                    </div>
                    <div className="bg-black p-3">
                      <div className="font-mono text-[11px] sm:text-[10px] sm:text-[9px] text-zinc-500 uppercase tracking-widest">Regulators</div>
                      <div className="font-mono text-xl text-white mt-1">{playbookOpen.regulators.length}</div>
                      <div className="font-mono text-[11px] sm:text-[10px] sm:text-[9px] text-zinc-500 mt-0.5">In scope</div>
                    </div>
                    <div className="bg-black p-3">
                      <div className="font-mono text-[11px] sm:text-[10px] sm:text-[9px] text-zinc-500 uppercase tracking-widest">Refreshed</div>
                      <div className="font-mono text-[12px] text-white mt-1.5">{playbookOpen.lastRefreshed || "—"}</div>
                      <div className="font-mono text-[11px] sm:text-[10px] sm:text-[9px] text-zinc-500 mt-0.5">Last circular sync</div>
                    </div>
                  </div>
                </div>

                <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-2">// What this rulebook does</div>
                <p className="text-sm text-zinc-300 leading-relaxed mb-6">
                  {playbookOpen.extendedDesc || playbookOpen.desc}
                </p>

                {playbookOpen.dataFlow && (
                  <>
                    <div className="font-mono text-[11px] sm:text-[10px] uppercase tracking-widest text-[#FFB000] mb-2">// Data flow</div>
                    <ul className="space-y-1.5 mb-6">
                      {playbookOpen.dataFlow.map((row, idx) => (
                        <li key={idx} className="font-mono text-xs sm:text-[11px] text-zinc-400 leading-relaxed flex items-start gap-2">
                          <span className="text-[#00E676] mt-[2px]">▸</span>
                          <span>{row}</span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                <div className="border-t border-zinc-900 pt-4 mt-4 flex flex-wrap gap-3">
                  <button
                    type="button"
                    onClick={() => { setPlaybookOpen(null); nav("/signup"); }}
                    data-testid="playbook-lightbox-cta-trial"
                    className="bg-[#FFB000] text-black font-bold font-mono text-[11px] uppercase tracking-wider px-4 py-2 hover:bg-[#E59E00] transition-colors flex items-center gap-2"
                  >
                    Run this playbook <ArrowRight className="w-3 h-3" />
                  </button>
                  <button
                    type="button"
                    onClick={() => { setPlaybookOpen(null); setCallDialog({ open: true, interest: "enterprise" }); }}
                    data-testid="playbook-lightbox-cta-analyst"
                    className="border border-[#FFB000]/40 text-[#FFB000] font-mono text-[11px] uppercase tracking-wider px-4 py-2 hover:bg-[#FFB000]/10 transition-colors"
                  >
                    Brief an analyst
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <BookDemoButton source="landing_page" variant="floating" />
    </div>
  );
}
